import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import HistoryIcon from '@mui/icons-material/History';
import { useReleaseNotesApi } from '../api/useReleaseNotesApi';
import { ReleaseNote, ReleaseNoteInput } from '../state/types';
import RoleBasedAccess, { userHasAccess } from '../services/RoleBasedAccess';
import { useSession } from '../context/SessionContext';
import ReleaseNoteModal from '../components/info/ReleaseNoteModal';

const formatDate = (iso: string) => {
  if (!iso) return '';
  // Treat YYYY-MM-DD as a calendar date in the local zone (avoid UTC drift).
  const [year, month, day] = iso.split('-').map(Number);
  if (year && month && day) {
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleDateString();
};

const surfaceCardSx = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === 'dark';
  return {
    p: { xs: 2.5, md: 4 },
    borderRadius: '18px',
    border: `1px solid ${alpha(isDark ? '#fff' : '#000', isDark ? 0.06 : 0.08)}`,
    backgroundColor: isDark ? '#141416' : '#fff',
    backgroundImage: isDark
      ? `linear-gradient(180deg, ${alpha('#fff', 0.025)} 0%, ${alpha('#fff', 0)} 38%)`
      : `linear-gradient(180deg, ${alpha('#000', 0.015)} 0%, ${alpha('#000', 0)} 40%)`,
    boxShadow: isDark
      ? '0 1px 0 rgba(255,255,255,0.04) inset, 0 12px 32px -16px rgba(0,0,0,0.6)'
      : '0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -20px rgba(15,23,42,0.15)',
  };
};

const eyebrowSx: SxProps<Theme> = {
  color: 'text.secondary',
  letterSpacing: '0.12em',
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
};

const Info: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user } = useSession();
  const isAdmin = userHasAccess(user?.role, ['Admin']);

  const { listReleaseNotesApi, createReleaseNoteApi, updateReleaseNoteApi, deleteReleaseNoteApi } =
    useReleaseNotesApi();

  const [notes, setNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<ReleaseNote | null>(null);

  const loadNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listReleaseNotesApi();
      setNotes(result);
    } catch (err) {
      console.error('Failed to load release notes', err);
      setError('Could not load release notes.');
    } finally {
      setLoading(false);
    }
  }, [listReleaseNotesApi]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleSave = async (input: ReleaseNoteInput, noteId?: string) => {
    if (noteId) {
      await updateReleaseNoteApi(noteId, input);
    } else {
      await createReleaseNoteApi(input);
    }
    await loadNotes();
  };

  const handleDelete = async (noteId: string) => {
    await deleteReleaseNoteApi(noteId);
    await loadNotes();
  };

  const openNew = () => {
    setEditingNote(null);
    setModalOpen(true);
  };

  const openEdit = (note: ReleaseNote) => {
    setEditingNote(note);
    setModalOpen(true);
  };

  const accentRed = theme.palette.status.accent;

  const primaryButtonSx: SxProps<Theme> = {
    backgroundColor: accentRed,
    color: '#fff',
    fontWeight: 600,
    letterSpacing: '0.01em',
    textTransform: 'none',
    borderRadius: '10px',
    px: 2.25,
    py: 0.9,
    boxShadow: `0 1px 0 ${alpha('#fff', 0.06)} inset, 0 6px 16px -8px ${alpha(accentRed, 0.6)}`,
    transition: 'box-shadow 160ms ease, transform 160ms ease',
    '&:hover': {
      backgroundColor: accentRed,
      boxShadow: `0 0 0 4px ${alpha(accentRed, 0.18)}, 0 8px 24px -8px ${alpha(accentRed, 0.7)}`,
    },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: `0 0 0 4px ${alpha(accentRed, 0.32)}`,
    },
  };

  const statusLegend = useMemo(
    () => [
      {
        label: 'Complete',
        color: theme.palette.status.complete,
        icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
        description: 'The step finished successfully.',
      },
      {
        label: 'Pending',
        color: theme.palette.status.pending,
        icon: null,
        description: 'Queued and waiting for a worker to pick it up.',
      },
      {
        label: 'Retrying',
        color: theme.palette.status.retrying,
        icon: <AutorenewIcon sx={{ fontSize: 14 }} />,
        description: 'A previous attempt failed and is being retried automatically.',
      },
      {
        label: 'Error',
        color: theme.palette.status.error,
        icon: <ErrorIcon sx={{ fontSize: 14 }} />,
        description: 'The step failed and is no longer being retried. Manual intervention may be required.',
      },
      {
        label: 'Not Started',
        color: theme.palette.status.notStarted,
        icon: null,
        description: 'No work has been done on this step yet.',
      },
    ],
    [theme.palette.status],
  );

  const accentSwatches = useMemo(
    () => [
      {
        label: 'UMS Accent (Red)',
        color: accentRed,
        description: 'Highlights primary actions, focused inputs, and the active row indicator in tables.',
      },
    ],
    [accentRed],
  );

  const iconLegend = useMemo(
    () => [
      {
        icon: <CheckCircleIcon sx={{ fontSize: 18 }} />,
        color: theme.palette.status.complete,
        label: 'Step finished successfully.',
      },
      {
        icon: <AutorenewIcon sx={{ fontSize: 18 }} />,
        color: theme.palette.status.retrying,
        label: 'Step is being retried after a failure.',
      },
      {
        icon: <ErrorIcon sx={{ fontSize: 18 }} />,
        color: theme.palette.status.error,
        label: 'Step failed and needs attention.',
      },
    ],
    [theme.palette.status],
  );

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        position: 'relative',
        backgroundColor: isDark ? '#0b0b0d' : '#f4f4f6',
        backgroundImage: isDark
          ? `radial-gradient(900px circle at 12% -10%, ${alpha('#fff', 0.04)} 0%, transparent 55%),
             radial-gradient(700px circle at 95% 100%, ${alpha(accentRed, 0.05)} 0%, transparent 60%)`
          : `radial-gradient(900px circle at 12% -10%, ${alpha('#000', 0.03)} 0%, transparent 55%),
             radial-gradient(700px circle at 95% 100%, ${alpha(accentRed, 0.04)} 0%, transparent 60%)`,
      }}
    >
      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 3, md: 5 } }}>
        {/* Page header */}
        <Stack spacing={1} sx={{ mb: { xs: 3, md: 4 } }}>
          <Typography sx={eyebrowSx}>App information</Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, letterSpacing: '-0.01em', fontSize: { xs: '1.6rem', md: '1.9rem' } }}
          >
            Release notes & legend
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 640 }}>
            Track what shipped in each release and reference the color and icon system used across BWI UMS.
          </Typography>
        </Stack>

        <Stack spacing={{ xs: 3, md: 4 }}>
          {/* Release notes */}
          <Paper elevation={0} sx={surfaceCardSx(theme)}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              gap={2}
              sx={{ mb: notes.length || loading || error ? 3 : 0 }}
            >
              <Stack spacing={0.5}>
                <Typography sx={eyebrowSx}>What's new</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.005em' }}>
                  Release notes
                </Typography>
              </Stack>
              <RoleBasedAccess allowedRoles={['Admin']}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openNew}
                  sx={primaryButtonSx}
                  data-cy="release-note-add-btn"
                >
                  Add release
                </Button>
              </RoleBasedAccess>
            </Stack>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  borderRadius: '12px',
                  border: `1px solid ${alpha(theme.palette.status.error, 0.35)}`,
                  backgroundColor: alpha(theme.palette.status.error, isDark ? 0.12 : 0.08),
                }}
              >
                {error}
              </Alert>
            )}

            {loading ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Loading release notes…
              </Typography>
            ) : notes.length === 0 ? (
              <Box
                sx={{
                  py: { xs: 4, md: 6 },
                  px: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  borderRadius: '14px',
                  border: `1px dashed ${alpha(isDark ? '#fff' : '#000', 0.1)}`,
                  backgroundColor: alpha(isDark ? '#fff' : '#000', isDark ? 0.015 : 0.02),
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: alpha(isDark ? '#fff' : '#000', isDark ? 0.05 : 0.04),
                    color: 'text.secondary',
                    mb: 2,
                  }}
                >
                  <HistoryIcon sx={{ fontSize: 28 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  No release notes yet
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', maxWidth: 380, mb: isAdmin ? 2.5 : 0 }}
                >
                  When the team ships an update, the changelog will appear here so everyone can see what changed.
                </Typography>
                <RoleBasedAccess allowedRoles={['Admin']}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openNew}
                    sx={primaryButtonSx}
                    data-cy="release-note-add-btn-empty"
                  >
                    Add the first release
                  </Button>
                </RoleBasedAccess>
              </Box>
            ) : (
              <Box component="ol" sx={{ pl: 0, m: 0, listStyle: 'none' }} data-cy="release-notes-list">
                {notes.map((note, index) => (
                  <Box
                    component="li"
                    key={note.noteId}
                    sx={{
                      position: 'relative',
                      pl: { xs: 2.5, sm: 3 },
                      pb: index === notes.length - 1 ? 0 : 3,
                      borderLeft: `1px solid ${alpha(isDark ? '#fff' : '#000', 0.08)}`,
                      ml: 1,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: -5,
                        top: 8,
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        backgroundColor: accentRed,
                        boxShadow: `0 0 0 3px ${alpha(accentRed, 0.18)}`,
                      },
                    }}
                    data-cy={`release-note-${note.noteId}`}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.25}
                      sx={{ flexWrap: 'wrap' }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                      >
                        v{note.version}
                      </Typography>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 500,
                          color: 'text.secondary',
                          backgroundColor: alpha(isDark ? '#fff' : '#000', isDark ? 0.06 : 0.05),
                          border: `1px solid ${alpha(isDark ? '#fff' : '#000', 0.06)}`,
                        }}
                      >
                        {formatDate(note.releaseDate)}
                      </Box>
                      {isAdmin && (
                        <Tooltip title="Edit release note">
                          <IconButton
                            size="small"
                            onClick={() => openEdit(note)}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'text.primary',
                                backgroundColor: alpha(isDark ? '#fff' : '#000', 0.06),
                              },
                            }}
                            data-cy={`release-note-edit-${note.noteId}`}
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    <Box component="ul" sx={{ pl: 2.5, mt: 1, mb: 0 }}>
                      {note.bullets.map((bullet, i) => (
                        <Box
                          component="li"
                          key={i}
                          sx={{
                            mb: 0.5,
                            color: 'text.primary',
                            '&::marker': {
                              color: alpha(isDark ? '#fff' : '#000', 0.4),
                            },
                          }}
                        >
                          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                            {bullet}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          {/* Legend */}
          <Paper elevation={0} sx={surfaceCardSx(theme)}>
            <Stack spacing={0.5} sx={{ mb: 3 }}>
              <Typography sx={eyebrowSx}>Reference</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.005em' }}>
                Legend
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Colors, pills, and icons used throughout the BWI UMS console.
              </Typography>
            </Stack>

            {/* Status pills */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Status pills
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Appear on asset status tags to indicate where a step is in its lifecycle.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' },
                  rowGap: 1.5,
                  columnGap: 3,
                  alignItems: 'center',
                }}
              >
                {statusLegend.map((entry) => (
                  <React.Fragment key={entry.label}>
                    <Box>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          backgroundColor: entry.color,
                          color: theme.palette.getContrastText(entry.color),
                          borderRadius: '999px',
                          px: 1.25,
                          py: 0.4,
                          fontSize: '11.5px',
                          fontWeight: 600,
                          letterSpacing: '0.01em',
                          minHeight: 22,
                          boxShadow: isDark
                            ? `inset 0 1px 0 ${alpha('#fff', 0.18)}`
                            : `inset 0 -1px 0 ${alpha('#000', 0.08)}`,
                        }}
                      >
                        {entry.icon}
                        <span>{entry.label}</span>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                      {entry.description}
                    </Typography>
                  </React.Fragment>
                ))}
              </Box>
            </Box>

            <Divider sx={{ borderColor: alpha(isDark ? '#fff' : '#000', 0.06), my: 3 }} />

            {/* Accent colors */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Accent colors
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Reserved for primary actions and the active state in tables and forms.
              </Typography>
              <Stack spacing={1.5}>
                {accentSwatches.map((entry) => (
                  <Box
                    key={entry.label}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '32px 1fr', sm: '32px 180px 1fr' },
                      gap: 2,
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        backgroundColor: entry.color,
                        boxShadow: `0 0 0 1px ${alpha(isDark ? '#fff' : '#000', 0.08)}, 0 4px 14px -6px ${alpha(entry.color, 0.55)}`,
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {entry.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                      {entry.description}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            <Divider sx={{ borderColor: alpha(isDark ? '#fff' : '#000', 0.06), my: 3 }} />

            {/* Icons */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Icons
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Compact glyphs used inside status pills and inline indicators.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '36px 1fr', sm: '36px 1fr' },
                  rowGap: 1.25,
                  columnGap: 2,
                  alignItems: 'center',
                }}
              >
                {iconLegend.map((entry, i) => (
                  <React.Fragment key={i}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: entry.color,
                        backgroundColor: alpha(entry.color, isDark ? 0.12 : 0.1),
                        border: `1px solid ${alpha(entry.color, 0.25)}`,
                      }}
                    >
                      {entry.icon}
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                      {entry.label}
                    </Typography>
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </Paper>
        </Stack>
      </Box>

      <ReleaseNoteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        note={editingNote}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </Box>
  );
};

export default Info;
